import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MailIcon, LockIcon, UserIcon, PhoneIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
type AuthTab = 'login' | 'signup' | 'otp';
export function LoginPage() {
  const navigate = useNavigate();
  const {
    login,
    signup,
    user
  } = useStore();
  const [activeTab, setActiveTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  // Signup state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupError, setSignupError] = useState('');
  // OTP state
  const [otpPhone, setOtpPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  if (user && !user.isAdmin) {
    navigate('/');
    return null;
  }
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!loginEmail.trim()) {
      setLoginError('Email is required');
      return;
    }
    if (!loginPassword.trim()) {
      setLoginError('Password is required');
      return;
    }
    const success = login(loginEmail, loginPassword);
    if (success) {
      navigate(-1);
    } else {
      setLoginError('Invalid credentials');
    }
  };
  const handleSignup = (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    if (!signupName.trim()) {
      setSignupError('Name is required');
      return;
    }
    if (!signupEmail.trim()) {
      setSignupError('Email is required');
      return;
    }
    if (!signupPassword.trim() || signupPassword.length < 6) {
      setSignupError('Password must be at least 6 characters');
      return;
    }
    signup(signupName, signupEmail, signupPhone || undefined);
    navigate('/');
  };
  const handleOtpLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpSent) {
      if (!otpPhone.trim() || otpPhone.length !== 10) {
        setOtpError('Enter valid 10-digit number');
        return;
      }
      setOtpSent(true);
      setOtpError('');
      return;
    }
    if (otpValue.length !== 4) {
      setOtpError('Enter 4-digit OTP');
      return;
    }
    signup(`User_${otpPhone.slice(-4)}`, `${otpPhone}@phone.skybrand.com`, otpPhone);
    navigate('/');
  };
  const tabs: {
    key: AuthTab;
    label: string;
  }[] = [{
    key: 'login',
    label: 'Login'
  }, {
    key: 'signup',
    label: 'Sign Up'
  }, {
    key: 'otp',
    label: 'OTP Login'
  }];
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-id="element-1087">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="w-full max-w-md" data-id="element-1088">
        {/* Brand Header */}
        <div className="text-center mb-8" data-id="element-1089">
          <img src="https://cdn.magicpatterns.com/uploads/njJUJ1uVWXUUEU8dnak6Jg/Screenshot_2025-12-23-16-07-24-10_99c04817c0de5652397fc8b56c3b3817.png" alt="Sky Brand" className="w-16 h-16 mx-auto mb-3 rounded-full" data-id="element-1090" />
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-1091">
            Welcome to Sky Brand
          </h1>
          <p className="text-slate-500 text-sm mt-1" data-id="element-1092">
            Sign in to continue shopping
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-1093">
          {/* Tabs */}
          <div className="flex border-b border-slate-200" data-id="element-1094">
            {tabs.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex-1 py-3.5 text-sm font-medium transition-colors relative ${activeTab === tab.key ? 'text-sky-600' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1095">
                {tab.label}
                {activeTab === tab.key && <motion.div layoutId="auth-tab" className="absolute bottom-0 inset-x-0 h-0.5 bg-sky-600" data-id="element-1096" />}
              </button>)}
          </div>

          <div className="p-6" data-id="element-1097">
            {/* Login Form */}
            {activeTab === 'login' && <form onSubmit={handleLogin} className="space-y-4" data-id="element-1098">
                <div data-id="element-1099">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1100">
                    Email
                  </label>
                  <div className="relative" data-id="element-1101">
                    <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1102" />
                    <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="you@example.com" data-id="element-1103" />
                  </div>
                </div>
                <div data-id="element-1104">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1105">
                    Password
                  </label>
                  <div className="relative" data-id="element-1106">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1107" />
                    <input type={showPassword ? 'text' : 'password'} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="Enter password" data-id="element-1108" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-1109">
                      {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-1110" /> : <EyeIcon className="w-4 h-4" data-id="element-1111" />}
                    </button>
                  </div>
                </div>
                {loginError && <p className="text-red-500 text-sm" data-id="element-1112">{loginError}</p>}
                <button type="submit" className="w-full py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1113">
                  Login
                </button>
                <p className="text-xs text-slate-400 text-center" data-id="element-1114">
                  Use any email & password to login as customer
                </p>
              </form>}

            {/* Signup Form */}
            {activeTab === 'signup' && <form onSubmit={handleSignup} className="space-y-4" data-id="element-1115">
                <div data-id="element-1116">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1117">
                    Full Name *
                  </label>
                  <div className="relative" data-id="element-1118">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1119" />
                    <input type="text" value={signupName} onChange={e => setSignupName(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="Your name" data-id="element-1120" />
                  </div>
                </div>
                <div data-id="element-1121">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1122">
                    Email *
                  </label>
                  <div className="relative" data-id="element-1123">
                    <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1124" />
                    <input type="email" value={signupEmail} onChange={e => setSignupEmail(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="you@example.com" data-id="element-1125" />
                  </div>
                </div>
                <div data-id="element-1126">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1127">
                    Phone (optional)
                  </label>
                  <div className="relative" data-id="element-1128">
                    <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1129" />
                    <input type="tel" value={signupPhone} onChange={e => setSignupPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="10-digit number" data-id="element-1130" />
                  </div>
                </div>
                <div data-id="element-1131">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1132">
                    Password *
                  </label>
                  <div className="relative" data-id="element-1133">
                    <LockIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1134" />
                    <input type="password" value={signupPassword} onChange={e => setSignupPassword(e.target.value)} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="Min 6 characters" data-id="element-1135" />
                  </div>
                </div>
                {signupError && <p className="text-red-500 text-sm" data-id="element-1136">{signupError}</p>}
                <button type="submit" className="w-full py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1137">
                  Create Account
                </button>
              </form>}

            {/* OTP Login */}
            {activeTab === 'otp' && <form onSubmit={handleOtpLogin} className="space-y-4" data-id="element-1138">
                <div data-id="element-1139">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1140">
                    Mobile Number
                  </label>
                  <div className="relative" data-id="element-1141">
                    <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1142" />
                    <input type="tel" value={otpPhone} onChange={e => {
                  setOtpPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setOtpSent(false);
                }} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="10-digit mobile number" disabled={otpSent} data-id="element-1143" />
                  </div>
                </div>
                {otpSent && <div data-id="element-1144">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1145">
                      Enter OTP
                    </label>
                    <input type="text" value={otpValue} onChange={e => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 4))} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-center tracking-[0.5em] font-mono focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="• • • •" maxLength={4} autoFocus data-id="element-1146" />
                    <p className="text-xs text-slate-400 mt-1" data-id="element-1147">
                      Enter any 4 digits (demo)
                    </p>
                  </div>}
                {otpError && <p className="text-red-500 text-sm" data-id="element-1148">{otpError}</p>}
                <button type="submit" className="w-full py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1149">
                  {otpSent ? 'Verify OTP' : 'Send OTP'}
                </button>
              </form>}
          </div>
        </div>
      </motion.div>
    </div>;
}